import type { DateOnly, Iso, TimeOnly } from "@shared/api"

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * NINGUNA FUNCIÓN DE ESTE ARCHIVO ACEPTA UN `Date` PARA VALORES DE RELOJ DE PARED.
 * NUNCA pases proposed_date / proposed_time / occurrence_date por new Date().
 *
 * `new Date('2026-08-03')` parsea como MEDIANOCHE UTC, así que todo el que esté al
 * oeste de UTC vería 2 de agosto — el off-by-one clásico, y además contradiría todas
 * las vistas SQL. Por eso formatWallDate/formatWallTime trabajan sobre las PARTES del
 * string y sus firmas piden DateOnly/TimeOnly, no Date.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * LA REGLA GENERAL: el browser NUNCA convierte zonas. Postgres hace toda la
 * aritmética (con `at time zone`, igual que el generador de recurrencias), la API
 * manda strings ya resueltos, y acá solo se FORMATEA.
 *
 * Se usan tres cosas nativas y nada más: Intl.DateTimeFormat con timeZone, resta de
 * milisegundos, y slicing de strings. NO se usa date-fns —aunque calendar-app sí lo
 * use— y no por los ~20 kB: traerlo invita a escribir
 * `format(new Date(row.proposed_date), 'PP')`, que es justo el bug que la regla de
 * arriba existe para evitar. calendar-app lo necesita porque hace aritmética de grilla
 * de calendario; este tablero no hace ninguna.
 */

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"]

// ─────────────────────────────────────────────────────────────────────────────
// Regla 1 — reloj de pared, formateado desde las partes del string
// ─────────────────────────────────────────────────────────────────────────────

/** '2026-08-03' → '3 ago 2026'. Sin Date, sin zonas. */
export function formatWallDate(d: DateOnly | null | undefined): string {
  if (!d) return "—"
  const [y, m, day] = d.split("-")
  const mi = Number(m) - 1
  if (!y || Number.isNaN(mi) || !MESES[mi]) return d
  return `${Number(day)} ${MESES[mi]} ${y}`
}

/** '2026-08-03' → '3 ago' (para ejes y celdas angostas). */
export function formatWallDateShort(d: DateOnly | null | undefined): string {
  if (!d) return "—"
  const [, m, day] = d.split("-")
  const mi = Number(m) - 1
  if (Number.isNaN(mi) || !MESES[mi]) return d
  return `${Number(day)} ${MESES[mi]}`
}

/** '14:30:00' → '2:30 PM'. Sin Date, sin zonas. */
export function formatWallTime(t: TimeOnly | null | undefined): string {
  if (!t) return "—"
  const [hStr, mStr] = t.split(":")
  const h = Number(hStr)
  if (Number.isNaN(h)) return t
  const suffix = h >= 12 ? "PM" : "AM"
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${mStr ?? "00"} ${suffix}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Regla 2 — la zona propia de la reunión, al lado de su hora
//
// Filas distintas pueden tener distinto proposed_timezone, así que un '2:30 PM'
// pelado no es comparable entre filas.
//
// La abreviatura se deriva del INSTANTE (proposed_at) y no del reloj de pared: es lo
// que hace que EST/EDT salga bien según la época del año.
// ─────────────────────────────────────────────────────────────────────────────

const abbrevCache = new Map<string, string>()

export function zoneAbbrev(instant: Iso | null | undefined, timeZone: string): string {
  if (!instant || !timeZone) return ""
  const key = `${instant.slice(0, 7)}|${timeZone}` // el mes alcanza: el DST no cambia dentro de uno
  const cached = abbrevCache.get(key)
  if (cached !== undefined) return cached

  let abbrev = ""
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(new Date(instant))
    abbrev = parts.find((p) => p.type === "timeZoneName")?.value ?? ""
  } catch {
    abbrev = ""
  }
  abbrevCache.set(key, abbrev)
  return abbrev
}

/** '3 ago 2026 · 2:30 PM EDT' — la forma canónica de mostrar una reunión. */
export function formatProposed(row: {
  proposed_date: DateOnly
  proposed_time: TimeOnly
  proposed_timezone: string
  proposed_at: Iso
}): string {
  const abbrev = zoneAbbrev(row.proposed_at, row.proposed_timezone)
  return `${formatWallDate(row.proposed_date)} · ${formatWallTime(row.proposed_time)}${
    abbrev ? ` ${abbrev}` : ""
  }`
}

// ─────────────────────────────────────────────────────────────────────────────
// Regla 3 — los instantes solo para ordenar y para tiempo transcurrido, renderizado
// como DURACIÓN (que es libre de zona por construcción)
// ─────────────────────────────────────────────────────────────────────────────

export function formatRelative(instant: Iso | null | undefined, now = Date.now()): string {
  if (!instant) return "—"
  const t = Date.parse(instant)
  if (Number.isNaN(t)) return "—"
  const diff = now - t
  const future = diff < 0
  const abs = Math.abs(diff)
  const mins = Math.floor(abs / 60_000)
  let text: string
  if (mins < 1) text = "hace segundos"
  else if (mins < 60) text = `${mins} min`
  else if (mins < 60 * 24) text = `${Math.floor(mins / 60)} h`
  else if (mins < 60 * 24 * 30) text = `${Math.floor(mins / (60 * 24))} d`
  else text = `${Math.floor(mins / (60 * 24 * 30))} meses`

  if (mins < 1) return text
  return future ? `en ${text}` : `hace ${text}`
}

/** Milisegundos → '6 h 12 m'. Para el tiempo a decisión. */
export function formatDuration(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60_000))
  if (mins < 60) return `${mins} m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h < 24) return m ? `${h} h ${m} m` : `${h} h`
  const d = Math.floor(h / 24)
  return `${d} d ${h % 24} h`
}

/** Minutos → '2,5 h', con coma decimal (locale es-AR). */
export function formatHours(minutes: number): string {
  return `${(minutes / 60).toFixed(1).replace(".", ",")} h`
}

export function formatInt(n: number): string {
  return n.toLocaleString("es-AR")
}

// ─────────────────────────────────────────────────────────────────────────────
// Regla 6 — los instantes de auditoría, en UNA zona de display fija
//
// created_at / requested_at / decided_at son instantes del rastro de auditoría: dos
// admins tienen que leer el MISMO número. Se formatean en displayTimezone (la que
// sirve /api/config, default America/New_York) y la abreviatura va UNA VEZ en el
// header de la columna, no en cada celda.
// ─────────────────────────────────────────────────────────────────────────────

export function formatInstant(instant: Iso | null | undefined, displayTimezone: string): string {
  if (!instant) return "—"
  const t = Date.parse(instant)
  if (Number.isNaN(t)) return "—"
  try {
    const p = new Intl.DateTimeFormat("en-US", {
      timeZone: displayTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(new Date(t))
    const get = (type: string) => p.find((x) => x.type === type)?.value ?? ""
    const mi = Number(get("month")) - 1
    return `${Number(get("day"))} ${MESES[mi] ?? get("month")} ${get("year")} · ${get("hour")}:${get(
      "minute"
    )} ${get("dayPeriod")}`
  } catch {
    return instant
  }
}

/** Abreviatura de la zona de display, para el header de columna: 'ET', 'EDT'… */
export function displayZoneLabel(displayTimezone: string): string {
  return zoneAbbrev(new Date().toISOString(), displayTimezone) || displayTimezone
}

// ─────────────────────────────────────────────────────────────────────────────
// Reglas 4 y 7 — las claves de bucket las calcula el SERVIDOR; acá solo se formatea
// la etiqueta. Y los presets de rango se calculan en displayTimezone, no en la zona
// del browser: "últimos 30 días" tiene que dar los mismos YYYY-MM-DD para un admin en
// Buenos Aires que para uno en Miami, porque esos bordes se comparan server-side.
// ─────────────────────────────────────────────────────────────────────────────

/** '2026-08-01' → 'ago 2026'. Solo formatea: el bucket ya lo agrupó Postgres. */
export function monthLabel(month: DateOnly): string {
  const [y, m] = month.split("-")
  const mi = Number(m) - 1
  return `${MESES[mi] ?? m} ${y}`
}

/** 'YYYY-MM-DD' de hoy EN LA ZONA DADA. Nunca new Date().toISOString().slice(0,10). */
export function todayInZone(timeZone: string): DateOnly {
  try {
    const p = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date())
    const get = (t: string) => p.find((x) => x.type === t)?.value ?? "01"
    return `${get("year")}-${get("month")}-${get("day")}`
  } catch {
    return new Date().toISOString().slice(0, 10)
  }
}

/** Suma (o resta) días a un 'YYYY-MM-DD' con aritmética pura de UTC — sin zonas. */
export function addDays(d: DateOnly, days: number): DateOnly {
  const t = Date.parse(`${d}T00:00:00Z`)
  return new Date(t + days * 86_400_000).toISOString().slice(0, 10)
}

export function startOfMonth(d: DateOnly): DateOnly {
  return `${d.slice(0, 7)}-01`
}

export function endOfMonth(d: DateOnly): DateOnly {
  const [y, m] = d.split("-").map(Number)
  // Día 0 del mes siguiente = último del actual. Todo en UTC, así que no hay zonas.
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}

export type RangePresetId =
  | "today"
  | "last7"
  | "last30"
  | "last90"
  | "thisMonth"
  | "lastMonth"
  | "all"
  /** El usuario tipeó fechas a mano: rangePreset() no las toca. */
  | "custom"

export const RANGE_PRESETS: { id: RangePresetId; label: string }[] = [
  { id: "all", label: "Todo" },
  { id: "today", label: "Hoy" },
  { id: "last7", label: "Últimos 7 días" },
  { id: "last30", label: "Últimos 30 días" },
  { id: "last90", label: "Últimos 90 días" },
  { id: "thisMonth", label: "Mes en curso" },
  { id: "lastMonth", label: "Mes anterior" },
]

/** Preset → { from, to } en displayTimezone. `undefined` = sin borde. */
export function rangePreset(
  id: RangePresetId,
  displayTimezone: string
): { from?: DateOnly; to?: DateOnly } {
  const today = todayInZone(displayTimezone)
  switch (id) {
    case "all":
    // 'custom' no calcula nada: las fechas las puso el usuario a mano y el llamador
    // las conserva.
    case "custom":
      return {}
    case "today":
      return { from: today, to: today }
    case "last7":
      return { from: addDays(today, -6), to: today }
    case "last30":
      return { from: addDays(today, -29), to: today }
    case "last90":
      return { from: addDays(today, -89), to: today }
    case "thisMonth":
      return { from: startOfMonth(today), to: endOfMonth(today) }
    case "lastMonth": {
      const prev = addDays(startOfMonth(today), -1)
      return { from: startOfMonth(prev), to: endOfMonth(prev) }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase 2 — meses 'YYYY-MM', porcentajes y deltas. Mismas reglas: aritmética pura de
// strings/UTC, y las claves de bucket siempre vienen del servidor.
// ─────────────────────────────────────────────────────────────────────────────

/** 'YYYY-MM' de este mes EN LA ZONA DADA (los presets del rango de meses). */
export function currentMonthInZone(timeZone: string): string {
  return todayInZone(timeZone).slice(0, 7)
}

/** Suma (o resta) meses a un 'YYYY-MM'. Aritmética de calendario pura, sin Date. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number)
  const total = y * 12 + (m - 1) + n
  const ny = Math.floor(total / 12)
  const nm = (total % 12) + 1
  return `${ny}-${String(nm).padStart(2, "0")}`
}

/** '2026-08-03' (lunes que mandó el server) → 'Sem del 3 ago'. Solo formatea. */
export function weekLabel(weekStart: DateOnly): string {
  return `Sem del ${formatWallDateShort(weekStart)}`
}

/** 0.756 → '75,6 %'. null → '—' (una semana sin decisiones no es 0 %). */
export function formatPercent(rate: number | null | undefined, decimals = 1): string {
  if (rate === null || rate === undefined || Number.isNaN(rate)) return "—"
  return `${(rate * 100).toFixed(decimals).replace(".", ",")} %`
}

/** Delta firmado para stat tiles: signo SIEMPRE explícito ('+3', '−1,5'). */
export function formatDelta(n: number, format: (abs: number) => string = String): string {
  if (n === 0) return `±${format(0)}`
  return n > 0 ? `+${format(n)}` : `−${format(Math.abs(n))}`
}

/** Nombre del día de la semana para day_of_week (0 = domingo, como extract(dow)). */
export const WEEKDAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"]

export function weekdayName(dow: number | null | undefined): string {
  if (dow === null || dow === undefined) return "—"
  return WEEKDAYS[dow] ?? String(dow)
}
